import React from "react";
import PropTypes from "prop-types";
import { Stack, Typography, Box, IconButton, Chip } from "@mui/material";

import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import {
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "../wafEventDrawerShared";
import {
  spHeaderMarkSx,
  spHeaderPathSx,
  spHeaderIdValueSx,
  spIdChipSx,
  spMonoFontFamily,
  spMethodChipSx,
  spLh,
} from "./smartParseDrawerUi";

/**
 * Header for non-HTTP event drawers (findings, endpoints, etc.).
 * Reuses Smart Parse header typography without requiring method/path/eventId.
 */
export default function FindingDrawerHeader({
  title,
  subtitle,
  subtitleMonospace = true,
  icon = DETECTION_ICONS.detection,
  method,
  path,
  detailLine,
  idLabel,
  idValue,
  secondaryIdLabel,
  secondaryIdValue,
  onCopyId,
  embedded = false,
}) {
  const methodLabel = method ? String(method).toUpperCase() : null;
  const pathLabel = path || null;

  const renderIdChip = (label, value) => {
    if (!value) return null;
    return (
      <Box key={label || value} sx={spIdChipSx}>
        <Typography
          title={label ? `${label}: ${value}` : String(value)}
          sx={spHeaderIdValueSx}
        >
          {label ? `${label}: ` : ""}{value}
        </Typography>
        {onCopyId && (
          <IconButton
            size="small"
            onClick={() => onCopyId(value)}
            aria-label={`Copy ${label || "ID"}`}
            sx={{
              width: 22,
              height: 22,
              borderRadius: "6px",
              color: PRIMARY_COLOR,
              flexShrink: 0,
              "&:hover": { backgroundColor: "#EEF0FE" },
            }}
          >
            <LayerdIcon icon={DETECTION_ICONS.copy} size={13} color="primary" />
          </IconButton>
        )}
      </Box>
    );
  };

  const idChips = [renderIdChip(idLabel, idValue), renderIdChip(secondaryIdLabel, secondaryIdValue)].filter(Boolean);

  const content = (
    <Stack direction="row" spacing={0} sx={{ gap: "13px", minWidth: 0, flex: 1, position: "relative", zIndex: 1 }}>
      <Box sx={spHeaderMarkSx}>
        {typeof icon === "function" ? (
          <LayerdIcon icon={icon} size={22} color="primary" />
        ) : (
          React.createElement(icon, { sx: { fontSize: 22, color: PRIMARY_COLOR } })
        )}
      </Box>

      <Stack spacing={0} sx={{ flex: 1, minWidth: 0, pt: "1px" }}>
        <Typography
          component="h1"
          sx={{
            m: 0,
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: "-0.015em",
            lineHeight: spLh.title,
            color: PRIMARY_COLOR,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              mt: "7px",
              fontSize: subtitleMonospace ? 12.5 : 14,
              lineHeight: spLh.snug,
              color: subtitleMonospace ? PRIMARY_COLOR : TEXT_PRIMARY,
              fontFamily: subtitleMonospace ? spMonoFontFamily : "inherit",
              wordBreak: "break-all",
            }}
          >
            {subtitle}
          </Typography>
        )}

        {(methodLabel || pathLabel) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="nowrap"
            sx={{ mt: "7px", fontFamily: spMonoFontFamily, fontSize: 12.5, minWidth: 0 }}
          >
            {methodLabel && (
              <Chip label={methodLabel} size="small" sx={spMethodChipSx} />
            )}
            {pathLabel && (
              <Typography component="span" title={pathLabel} sx={spHeaderPathSx}>
                {pathLabel}
              </Typography>
            )}
          </Stack>
        )}

        {detailLine && (
          <Typography sx={{ mt: "7px", fontSize: 14, lineHeight: spLh.px21, color: TEXT_SECONDARY }}>
            {detailLine}
          </Typography>
        )}

        {idChips.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: "11px", gap: 1 }}>
            {idChips}
          </Stack>
        )}
      </Stack>
    </Stack>
  );

  if (embedded) return content;
  return content;
}

FindingDrawerHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  subtitleMonospace: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  method: PropTypes.string,
  path: PropTypes.string,
  detailLine: PropTypes.string,
  idLabel: PropTypes.string,
  idValue: PropTypes.string,
  secondaryIdLabel: PropTypes.string,
  secondaryIdValue: PropTypes.string,
  onCopyId: PropTypes.func,
  embedded: PropTypes.bool,
};
